import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api, useAuth } from '../App';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState('checking');
  const [creditsAdded, setCreditsAdded] = useState(0);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async (attempt = 0) => {
      if (attempt >= 8) {
        setStatus('timeout');
        return;
      }

      try {
        const res = await api.get(`/api/credits/status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          setCreditsAdded(res.data.credits_added || 0);
          setStatus('success');
          return;
        } else if (res.data.status === 'expired') {
          setStatus('expired');
          return;
        }
      } catch {
        // continue polling
      }

      setAttempts(attempt + 1);
      setTimeout(() => pollStatus(attempt + 1), 2000);
    };

    pollStatus();
  }, [sessionId]);

  return (
    <div className="flex items-center justify-center min-h-[70vh] animate-fade-in" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <div className="card-interactive p-10">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <i className="fa-solid fa-spinner fa-spin text-primary text-3xl" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-text-primary mb-2">Processing Payment...</h2>
            <p className="text-text-muted text-sm">Verifying your purchase. This won't take long.</p>
            <div className="mt-4 flex justify-center gap-1">
              {[...Array(8)].map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${i <= attempts ? 'bg-primary' : 'bg-border'} transition-colors`} />
              ))}
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="card-interactive p-10">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 animate-slide-up">
              <i className="fa-solid fa-check text-primary text-3xl" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-text-primary mb-2">Payment Successful!</h2>
            <div className="my-6 p-4 rounded-xl bg-primary/5 border border-primary/20">
              <p className="text-4xl font-heading font-bold text-primary" data-testid="credits-added">+{creditsAdded.toLocaleString()}</p>
              <p className="text-xs text-text-muted mt-1">credits added to your account</p>
            </div>
            <p className="text-text-muted text-sm mb-6">Your credits are ready to use. Start mining leads, enriching data, and closing deals!</p>
            <div className="flex gap-3 justify-center">
              <Link to="/" className="btn-primary text-sm py-2.5 px-6" data-testid="go-dashboard"><i className="fa-solid fa-chart-pie mr-2" />Dashboard</Link>
              <Link to="/scanner" className="btn-secondary text-sm py-2.5 px-6" data-testid="go-scanner"><i className="fa-solid fa-tower-broadcast mr-2" />Start Scanning</Link>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'timeout' || status === 'expired') && (
          <div className="card-interactive p-10">
            <div className="w-20 h-20 rounded-full bg-destructive-light flex items-center justify-center mx-auto mb-6">
              <i className="fa-solid fa-exclamation-triangle text-destructive text-3xl" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-text-primary mb-2">
              {status === 'timeout' ? 'Verification Timeout' : status === 'expired' ? 'Session Expired' : 'Payment Error'}
            </h2>
            <p className="text-text-muted text-sm mb-6">
              {status === 'timeout' ? 'Payment verification is taking longer than expected. Credits will be added once confirmed.' : 'Something went wrong. Please try again or contact support.'}
            </p>
            <Link to="/credits" className="btn-primary text-sm py-2.5 px-6" data-testid="try-again"><i className="fa-solid fa-rotate-left mr-2" />Try Again</Link>
          </div>
        )}
      </div>
    </div>
  );
}
